import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import YearEditModal from './YearEditModal';

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
}

export default function YearsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('years_study', 'write');
  const canDelete = can('years_study', 'delete');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | year object

  const load = () => {
    setLoading(true); setError(null);
    api.get('/api/years_study')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onDelete = async (year) => {
    if (!canDelete) return;
    if (!confirm(`Удалить учебный год «${year.name}»?\nПривязанные договоры останутся (year_study_id обнулится).`)) return;
    try {
      await api.delete(`/api/years_study/${year.id}`);
      setRows((prev) => prev.filter((r) => r.id !== year.id));
    } catch (e) {
      alert('Не удалось удалить: ' + (e.message || ''));
    }
  };

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Учебные годы']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Учебные годы {!loading && <span className="muted">· {rows.length}</span>}
          </span>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setEditing('new')}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет права на создание'}
          >
            <Icon name="plus" size={13} /> Добавить год
          </button>
        </div>

        <div className="ref-content">
          {error && (
            <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>
          )}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">Учебных годов нет. Добавьте первый.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Название</th>
                    <th style={{ width: 200 }}>Период</th>
                    <th style={{ width: 140 }}>Сад до</th>
                    <th style={{ width: 110 }}>Триместров</th>
                    <th style={{ width: 100 }}>Статус</th>
                    <th style={{ width: 80 }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((y) => (
                    <tr key={y.id}>
                      <td>
                        <span style={{ fontWeight: 500 }}>{y.name}</span>
                      </td>
                      <td className="mono">
                        {fmtDate(y.dateStart)} — {fmtDate(y.dateEnd)}
                      </td>
                      <td className="mono">{fmtDate(y.dateEndKindergarten)}</td>
                      <td>{y.trimesterDates?.length || 0}</td>
                      <td>
                        {y.archive
                          ? <span className="sb sb--archived">Архив</span>
                          : <span className="sb sb--active">Активен</span>}
                      </td>
                      <td>
                        <div className="ref-row-actions">
                          <button
                            onClick={() => setEditing(y)}
                            title={canWrite ? 'Редактировать' : 'Просмотр'}
                          >
                            <Icon name="edit" size={14} />
                          </button>
                          {canDelete && (
                            <button className="is-danger" onClick={() => onDelete(y)} title="Удалить">
                              <Icon name="x" size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing === 'new' && (
        <YearEditModal
          mode="create"
          onClose={() => setEditing(null)}
          onSaved={(y) => { setRows((prev) => [y, ...prev]); }}
        />
      )}
      {editing && editing !== 'new' && (
        <YearEditModal
          mode="edit"
          year={editing}
          onClose={() => setEditing(null)}
          onSaved={(y) => setRows((prev) => prev.map((r) => r.id === y.id ? y : r))}
        />
      )}
    </>
  );
}
